import { AutocompleteProps, Box } from "@mui/material";
import {
  SmartAutoComplete,
  SmartForm,
  useSmartForm,
} from "@nodeon/react-components";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { usePortApi } from "~/api/GeneratedApi";
import { useCustomQuery } from "~/utils/hooks/useCustomQuery";

interface Props {
  portId?: number;
  onSubmit: (portId: number) => void;
}

interface FormProps {
  portId: number;
}

const BerthPortSelectForm = ({
  portId,
  onSubmit,
  ...rest
}: Props &
  Partial<
    Omit<AutocompleteProps<number, false, true, false>, "onSubmit">
  >): JSX.Element | null => {
  const { t } = useTranslation("berths");

  const portApi = usePortApi();

  const { data: ports, isLoading: portsLoading } = useCustomQuery(
    "ports",
    () => portApi.getPorts(),
    { select: (data) => data.results ?? [] },
  );

  const defaultValues = useMemo(
    () => ({
      portId: portId ?? ports?.[0]?.id ?? -1,
    }),
    [portId, ports],
  );

  const methods = useSmartForm({
    defaultValues,
  });

  const { watch, reset } = methods;
  const { portId: selectedPortId } = watch();

  React.useEffect(() => {
    if (portId) return;
    if (!ports || ports.length === 0) return;
    reset({ portId: ports[0].id });
  }, [portId, ports, reset]);

  React.useEffect(() => {
    if (!selectedPortId || selectedPortId === -1) return;
    onSubmit(selectedPortId);
  }, [onSubmit, selectedPortId]);

  if (selectedPortId === -1) return null;

  return (
    <Box width="40%">
      <SmartForm<FormProps>
        onSubmit={(data) => onSubmit(data.portId)}
        gridProps={{ justifyContent: "center", direction: "column" }}
        methods={methods}
      >
        <SmartAutoComplete<FormProps, number, false, true, false>
          {...rest}
          name="portId"
          options={ports?.map((x) => x.id) ?? []}
          controllerProps={{
            rules: {
              required: { value: true, message: t("form:required") },
            },
          }}
          label={t("port")}
          fullWidth
          loading={portsLoading}
          gridProps={{ xs: 8, lg: 6 }}
          getOptionLabel={(opt) => {
            const port = ports?.find((x) => x.id === opt);
            if (!port) return t("general:unknown");
            return port.name ?? t("general:unknown");
          }}
          disableClearable
        />
      </SmartForm>
    </Box>
  );
};

export default BerthPortSelectForm;
